import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { sectionsForRole } from '../content/guide';
import { colors, radius, spacing, typography } from '../config/theme';
import { FolkStrip, Pictogram, StickerBackdrop, Wordmark } from './brand';
import { screen } from './ui';

function Section({ section, open, onToggle }) {
  return (
    <View style={[styles.card, open && styles.cardOpen]}>
      <Pressable onPress={onToggle} style={styles.head}>
        <Pictogram name={section.icon} size={24} color={open ? colors.primary : colors.textPrimary} style={styles.headIcon} />
        <View style={styles.grow}>
          <Text style={styles.headTitle}>{section.title}</Text>
          {!open && section.intro ? <Text style={styles.headIntro} numberOfLines={2}>{section.intro}</Text> : null}
        </View>
        <Text style={styles.chevron}>{open ? '–' : '+'}</Text>
      </Pressable>
      {open ? (
        <View style={styles.body}>
          {section.intro ? <Text style={styles.bodyIntro}>{section.intro}</Text> : null}
          {section.steps.map((step, i) => (
            <View key={step.heading} style={styles.step}>
              <Text style={styles.stepNum}>{i + 1}</Text>
              <View style={styles.grow}>
                <Text style={styles.stepHeading}>{step.heading}</Text>
                <Text style={styles.stepBody}>{step.body}</Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

export default function HelpScreen() {
  const { isAdmin } = useAuth();
  const sections = useMemo(() => sectionsForRole({ isAdmin }), [isAdmin]);
  const [openId, setOpenId] = useState(sections[0]?.id ?? null);

  return (
    <View style={screen.root}>
      <StickerBackdrop seed="pamyatka" />
      <ScrollView contentContainerStyle={screen.content}>
      <View style={styles.hero}>
        <Wordmark height={20} color={colors.textPrimary} />
        <Text style={styles.title}>Памятка</Text>
        <Text style={styles.sub}>Как всё устроено — по разделам. Нажмите на раздел, чтобы раскрыть.</Text>
        <FolkStrip color={colors.borderStrong} opacity={0.4} />
      </View>
      {sections.map((s) => (
        <Section key={s.id} section={s} open={openId === s.id} onToggle={() => setOpenId((cur) => (cur === s.id ? null : s.id))} />
      ))}
      <Text style={styles.footer}>Не нашли ответ — напишите администратору в чат.</Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  hero: { gap: spacing.xs, marginBottom: spacing.sm },
  title: { ...typography.h1, color: colors.textPrimary, marginTop: spacing.xs },
  sub: { ...typography.body, color: colors.textSecondary },
  card: {
    backgroundColor: colors.surfaceCard,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  cardOpen: { borderColor: colors.primary },
  head: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, padding: spacing.md },
  headIcon: { marginTop: 1 },
  grow: { flex: 1 },
  headTitle: { ...typography.h3, color: colors.textPrimary },
  headIntro: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  chevron: { fontFamily: typography.h2.fontFamily, fontSize: 22, color: colors.primary, width: 18, textAlign: 'center' },
  body: { paddingHorizontal: spacing.md, paddingBottom: spacing.md, gap: spacing.md },
  bodyIntro: { ...typography.body, color: colors.textSecondary, fontStyle: 'italic' },
  step: { flexDirection: 'row', gap: spacing.md, alignItems: 'flex-start' },
  stepNum: {
    ...typography.caption,
    fontWeight: '800',
    color: colors.onPrimary,
    backgroundColor: colors.primary,
    width: 20,
    height: 20,
    borderRadius: 10,
    textAlign: 'center',
    lineHeight: 20,
    overflow: 'hidden',
  },
  stepHeading: { ...typography.body, fontWeight: '700', color: colors.textPrimary },
  stepBody: { ...typography.body, color: colors.textSecondary, marginTop: 1 },
  footer: { ...typography.caption, color: colors.textMuted, textAlign: 'center', marginTop: spacing.md },
});
